// Web → Analytika → Kvíz a kalkulátor.
//
// Kvíz: kolik žen dojde ke které otázce, jak dlouho jim to trvá, kolik času
// stráví nad jednotlivou otázkou a u které to vzdají. Otázka s největším
// propadem je ta, kterou je potřeba přeformulovat nebo vyhodit.
//
// Kalkulátor: co lidé opravdu mačkají a kolik času tam stráví.
import React, { useMemo } from "react";
import { HelpCircle, Calculator, AlertTriangle, ArrowDown, Users, Clock, Timer, MousePointerClick, Lightbulb, MailCheck } from "lucide-react";

const BRAND = "#6E519F";
const PATH_KALKULATOR = "/kalkulator/";

const pct = (a, b) => (b > 0 ? Math.round((a / b) * 1000) / 10 : 0);
const cas = (s) => {
  const n = Number(s) || 0;
  if (n <= 0) return null;
  if (n < 60) return `${n} s`;
  return `${Math.floor(n / 60)} m ${String(Math.round(n % 60)).padStart(2, "0")} s`;
};

// Nad kolik procent ztráty mezi dvěma otázkami už je to signál, ne šum.
const PRAH_VAROVNY = 12;
const PRAH_KRITICKY = 22;

function Delic() {
  return <span aria-hidden className="h-3 w-px shrink-0 bg-slate-300" />;
}

// Údaj v oválném štítku. Drží se pohromadě i při zalomení na mobilu,
// což řádek oddělený čárkami nedokázal.
const BARVY_STITKU = {
  slate: "border-slate-200 bg-white text-slate-600",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  rose: "border-rose-200 bg-rose-50 text-rose-700",
};

function Stitek({ children, barva = "slate", titul }) {
  return (
    <span
      title={titul}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${BARVY_STITKU[barva] || BARVY_STITKU.slate}`}
    >
      {children}
    </span>
  );
}

// Název kroku, ke kterému se zatím nikdo nedostal, takže nám měření
// nestihlo poslat jeho popisek. Kvíz má úvod, otázky, e-mail a výsledek.
function popisKroku(krok, celkem) {
  if (krok === 0) return "Úvodní obrazovka";
  if (celkem && krok === celkem) return "Výsledek testu";
  if (celkem && krok === celkem - 1) return "Zadání e-mailu";
  return `Otázka ${krok}`;
}

/**
 * Kolik času žena stráví nad svým výsledkem testu.
 *
 * Výsledek je poslední obrazovka kvízu a chodí se na ni i přímo odkazem
 * z e-mailu, takže tohle číslo říká, jestli si žena svůj typ opravdu čte,
 * nebo jen mrkne a zavře. Pod deset vteřin nestihla přečíst ani nadpis.
 *
 * Medián je hlavní číslo. Průměr rozhodí jedna žena, která nechá záložku
 * otevřenou přes oběd, medián ne.
 */
function CasNaVysledku({ d }) {
  const pasma = [
    { klic: "do10", popis: "do 10 s", hodnota: d.do10, spatne: true },
    { klic: "do30", popis: "10 až 30 s", hodnota: d.do30 },
    { klic: "do60", popis: "30 s až 1 m", hodnota: d.do60 },
    { klic: "do180", popis: "1 až 3 m", hodnota: d.do180, dobre: true },
    { klic: "nad180", popis: "nad 3 m", hodnota: d.nad180, dobre: true },
  ];
  const celkem = pasma.reduce((a, p) => a + (p.hodnota || 0), 0) || 1;
  const nejvyssi = Math.max(...pasma.map((p) => p.hodnota || 0), 1);
  const rychle = pct(d.do10 || 0, celkem);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <h3 className="mb-1 flex items-center gap-2 text-base font-semibold text-slate-900">
        <Timer className="w-4 h-4 text-brand-600" /> Čas na výsledku testu
      </h3>
      <p className="mb-4 text-xs leading-relaxed text-slate-500">
        Od chvíle, kdy se ženě ukázal její výsledek, do chvíle, kdy stránku zavřela.
        Chodí se sem i přímo z e-mailu, takže je to zároveň údaj o tom, jestli si výsledek čtou.
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
        <span className="text-slate-600">
          medián <strong className="tabular-nums text-lg text-slate-900">{cas(d.median_s) || "0 s"}</strong>
        </span>
        <Delic />
        <span className="text-slate-600">
          průměr <strong className="tabular-nums text-slate-900">{cas(d.prumer_s) || "0 s"}</strong>
        </span>
        <Delic />
        <span className="text-slate-600">
          z <strong className="tabular-nums text-slate-900">{d.zmereno}</strong> zobrazení výsledku
        </span>
        {d.videlo > d.zmereno && (
          <>
            <Delic />
            <span className="text-slate-400" title="Když prohlížeč stránku zavře dřív, než stihne odeslat poslední zprávu, čas se nezměří.">
              u {d.videlo - d.zmereno} se čas nezměřil
            </span>
          </>
        )}
      </div>

      <ul className="space-y-1.5">
        {pasma.map((p) => (
          <li key={p.klic} className="flex items-center gap-3 text-sm">
            <span className="w-24 shrink-0 text-slate-600">{p.popis}</span>
            <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
              <span
                className={`block h-full rounded-full ${
                  p.spatne ? "bg-rose-400" : p.dobre ? "bg-emerald-500" : "bg-brand-400"
                }`}
                style={{ width: `${Math.round(((p.hodnota || 0) / nejvyssi) * 100)}%` }}
              />
            </span>
            <span className="w-8 shrink-0 text-right tabular-nums font-medium text-slate-900">
              {p.hodnota || 0}
            </span>
            <span className="w-12 shrink-0 text-right tabular-nums text-slate-400">
              {pct(p.hodnota || 0, celkem)} %
            </span>
          </li>
        ))}
      </ul>

      {rychle >= 40 && (
        <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs leading-relaxed text-amber-900">
          {rychle} % žen zavře výsledek do deseti vteřin. Za tu dobu se nedá přečíst ani nadpis,
          takže je buď výsledek nezaujme, nebo čekaly něco jiného, než dostaly.
        </p>
      )}
    </div>
  );
}

// Barvy typů výsledku, ať jde jedním pohledem poznat, ke kterému typu
// odpověď vede. Stejné pojmenování jako v kvízu na webu.
const TYPY = {
  H: { popis: "Hormonální", barva: "bg-violet-500", text: "text-violet-700" },
  S: { popis: "Stresový", barva: "bg-amber-500", text: "text-amber-700" },
  J: { popis: "Jídelní", barva: "bg-emerald-500", text: "text-emerald-700" },
  B: { popis: "Jo-jo", barva: "bg-rose-500", text: "text-rose-700" },
};

/**
 * Nejčastější odpověď u každé otázky kvízu.
 *
 * Tohle je nejcennější věc, kterou z kvízu máme: přesná slova, kterými ženy
 * samy popisují svůj problém. Co tu vyhrává, to patří do nadpisů na prodejní
 * stránce a do prvních vět e-mailů.
 *
 * Počítají se různé návštěvnice, ne kliknutí, takže překlikáváním se číslo
 * nafouknout nedá.
 */
function NejcastejsiOdpovedi({ radky }) {
  const otazky = useMemo(() => {
    const mapa = new Map();
    for (const r of radky || []) {
      if (!mapa.has(r.krok)) mapa.set(r.krok, { krok: r.krok, otazka: r.otazka, volby: [] });
      mapa.get(r.krok).volby.push(r);
    }
    return [...mapa.values()]
      .sort((a, b) => a.krok - b.krok)
      .map((o) => {
        const celkem = o.volby.reduce((a, v) => a + (v.pocet || 0), 0);
        const volby = [...o.volby].sort((a, b) => b.pocet - a.pocet);
        return { ...o, celkem, volby };
      });
  }, [radky]);

  if (!otazky.length) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="mb-1 flex items-center gap-2 text-base font-semibold text-slate-900">
          <Lightbulb className="w-4 h-4 text-brand-600" /> Nejčastější odpovědi
        </h3>
        <p className="text-sm leading-relaxed text-slate-500">
          Zatím bez dat. Odpovědi se začaly měřit teprve teď, takže se tu objeví,
          jakmile kvíz vyplní první ženy. Starší vyplnění zpětně dopočítat nejde,
          web je dřív neposílal.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <h3 className="mb-1 flex items-center gap-2 text-base font-semibold text-slate-900">
        <Lightbulb className="w-4 h-4 text-brand-600" /> Nejčastější odpovědi
      </h3>
      <p className="mb-4 text-xs leading-relaxed text-slate-500">
        Co ženy u jednotlivých otázek opravdu zaškrtly. Vítězná odpověď je jejich vlastní
        popis problému, takže se hodí použít ji doslova v textech a e-mailech.
      </p>

      <div className="space-y-5">
        {otazky.map((o) => (
          <div key={o.krok}>
            <div className="mb-2 flex flex-wrap items-baseline gap-2">
              <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand-100 text-[11px] font-semibold text-brand-800">
                {o.krok}
              </span>
              <span className="text-sm font-semibold text-slate-900">{o.otazka || `Otázka ${o.krok}`}</span>
              <span aria-hidden className="h-3 w-px bg-slate-300" />
              <span className="text-xs text-slate-500">{o.celkem} odpovědí</span>
            </div>

            <ul className="space-y-1.5">
              {o.volby.map((v, i) => {
                const t = TYPY[v.typ] || { popis: v.typ, barva: "bg-slate-400", text: "text-slate-600" };
                const podil = pct(v.pocet, o.celkem);
                return (
                  <li key={v.typ} className="flex items-center gap-3 text-sm">
                    <span className={`w-56 shrink-0 truncate ${i === 0 ? "font-medium text-slate-900" : "text-slate-600"}`} title={v.odpoved || ""}>
                      {v.odpoved || t.popis}
                    </span>
                    <span className={`w-16 shrink-0 text-xs ${t.text}`}>{t.popis}</span>
                    <span className="h-2.5 flex-1 overflow-hidden rounded-full bg-slate-100">
                      <span className={`block h-full rounded-full ${t.barva}`} style={{ width: `${podil}%` }} />
                    </span>
                    <span className="w-8 shrink-0 text-right tabular-nums font-medium text-slate-900">{v.pocet}</span>
                    <span className="w-12 shrink-0 text-right tabular-nums text-slate-400">{podil} %</span>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DetailKviz({ data }) {
  const kroky = data?.kvizKroky || [];
  const odchody = data?.kvizOdchody || [];
  const akce = data?.akce || [];
  const casy = data?.casy || [];
  const casVysledku = data?.kvizCasVysledku || null;
  const odpovedi = data?.kvizOdpovedi || [];

  const rows = useMemo(() => {
    if (!kroky.length) return [];
    const mapaOdchodu = new Map(odchody.map((o) => [o.krok, o.skoncilo]));

    // Kvíz má pevný počet kroků, který nám hlásí měření v poli celkem.
    // Doplníme i ty, ke kterým se zatím nikdo nedostal, ať je vidět celá
    // cesta stejně jako u prodejní stránky, ne jen kroky s daty.
    const celkem = Math.max(...kroky.map((k) => k.celkem || 0), ...kroky.map((k) => k.krok || 0));
    const mapa = new Map(kroky.map((k) => [k.krok, k]));
    const plny = [];
    for (let n = 0; n <= celkem; n++) {
      plny.push(
        mapa.get(n) || { krok: n, popis: null, dosahlo: 0, dojiti_s: 0, celkem }
      );
    }

    // Mezi zadání e-mailu a výsledek testu patří ještě dva kroky, které kvíz
    // sám neměří, protože se dějí mimo něj: odeslání formuláře (žena skončí
    // na /kviz/dekuji/) a potvrzení e-mailu (/kviz/potvrzeno/). Teprve po
    // potvrzení jí dorazí odkaz na výsledek, takže výsledek testu je až za
    // nimi, ne hned po e-mailu. Bez těchhle dvou řádků vypadá cesta hotově,
    // i když se přesně tady ztrácí nejvíc lidí.
    const f = data?.funnel || {};
    const kvizove = plny.slice(0, Math.max(1, plny.length - 1));
    const vysledek = plny.length > 1 ? plny[plny.length - 1] : null;

    const seznam = [
      ...kvizove.map((k) => ({ ...k, typ: "krok" })),
      { typ: "mail", popis: "Odeslala e-mail", dosahlo: f.odeslano || 0, dojiti_s: 0, cesta: "/kviz/dekuji/" },
      { typ: "mail", popis: "Potvrdila e-mail", dosahlo: f.potvrzeno || 0, dojiti_s: 0, cesta: "/kviz/potvrzeno/" },
      ...(vysledek ? [{ ...vysledek, typ: "krok" }] : []),
    ];

    const zaklad = seznam[0]?.dosahlo || 0;

    return seznam.map((k, i) => {
      const dalsi = seznam[i + 1];
      const podil = pct(k.dosahlo, zaklad);
      const predchozi = i > 0 ? pct(seznam[i - 1].dosahlo, zaklad) : podil;
      // Čas nad otázkou je rozdíl mezi dojitím k další otázce a k této.
      const naOtazce =
        k.typ === "krok" && dalsi?.typ === "krok" && dalsi.dojiti_s > k.dojiti_s
          ? dalsi.dojiti_s - k.dojiti_s
          : 0;
      return {
        klic: k.typ === "krok" ? `k${k.krok}` : `m${k.cesta}`,
        typ: k.typ,
        cesta: k.cesta || null,
        cislo: k.typ === "krok" ? k.krok : null,
        popis: k.popis || popisKroku(k.krok, k.celkem || celkem),
        dosahlo: k.dosahlo,
        podil,
        propad: i === 0 ? 0 : Math.round((predchozi - podil) * 10) / 10,
        dojiti: k.dojiti_s || 0,
        naOtazce,
        skoncilo: k.typ === "krok" ? mapaOdchodu.get(k.krok) || 0 : 0,
        posledni: i === seznam.length - 1,
      };
    });
  }, [kroky, odchody, data]);

  const nejhorsi = useMemo(
    () => rows.filter((r) => r.propad >= PRAH_VAROVNY).sort((a, b) => b.propad - a.propad).slice(0, 3),
    [rows]
  );

  const dokonceni = useMemo(() => {
    if (!rows.length) return null;
    const prvni = rows[0]?.dosahlo || 0;
    const posledni = rows[rows.length - 1]?.dosahlo || 0;
    return { prvni, posledni, podil: pct(posledni, prvni) };
  }, [rows]);

  const kalkulator = useMemo(() => {
    const tlacitka = akce.filter((a) => a.path === PATH_KALKULATOR).sort((a, b) => b.kliku - a.kliku);
    const cas = casy.find((c) => c.path === PATH_KALKULATOR);
    const navsteva = (data?.stranky || []).find((p) => p.path === PATH_KALKULATOR);
    return { tlacitka, cas, navsteva };
  }, [akce, casy, data]);

  const maKviz = rows.some((r) => r.dosahlo > 0);
  const maKalkulator = kalkulator.tlacitka.length > 0 || kalkulator.cas || kalkulator.navsteva;

  return (
    <div className="space-y-5">
      {/* =============================================================== KVÍZ */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <HelpCircle className="w-5 h-5 text-brand-600" />
          <h2 className="text-lg font-semibold text-slate-900">Postup kvízem</h2>
        </div>

        {!maKviz && (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            Zatím nikdo kvíz nespustil. Data se objeví, jakmile někdo otevře první otázku.
          </div>
        )}

        {maKviz && (
          <>
            {dokonceni && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm">
                <span className="font-medium text-slate-900">Dokončení kvízu</span>
                <Delic />
                <span className="text-slate-600">
                  začalo <strong className="tabular-nums text-slate-900">{dokonceni.prvni}</strong>
                </span>
                <Delic />
                <span className="text-slate-600">
                  došlo na konec <strong className="tabular-nums text-slate-900">{dokonceni.posledni}</strong>
                </span>
                <Delic />
                <span
                  className={`font-semibold tabular-nums ${
                    dokonceni.podil >= 50 ? "text-emerald-700" : dokonceni.podil >= 30 ? "text-amber-600" : "text-rose-600"
                  }`}
                >
                  {dokonceni.podil} %
                </span>
              </div>
            )}

            {casVysledku && casVysledku.zmereno > 0 && (
              <CasNaVysledku d={casVysledku} />
            )}

            <NejcastejsiOdpovedi radky={odpovedi} />

            {!!nejhorsi.length && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                <h3 className="mb-2 flex items-center gap-2 text-base font-semibold text-amber-900">
                  <AlertTriangle className="w-4 h-4" /> Kde se ztrácí nejvíc žen
                </h3>
                <ul className="space-y-2">
                  {nejhorsi.map((r) => (
                    <li key={r.klic} className="text-sm text-amber-900">
                      <strong>{r.popis}</strong>
                      <span aria-hidden className="mx-2 inline-block h-3 w-px translate-y-0.5 bg-amber-900/40" />
                      ubylo {r.propad} %
                      {r.skoncilo > 0 && (
                        <>
                          <span aria-hidden className="mx-2 inline-block h-3 w-px translate-y-0.5 bg-amber-900/40" />
                          {r.skoncilo} tu skončilo
                        </>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-amber-800">
                  <Lightbulb className="mt-0.5 w-3.5 h-3.5 shrink-0" />
                  U otázky bývá důvod v ní samotné: je moc osobní, moc dlouhá, nebo v ní
                  chybí odpověď, která by na ženu seděla. U odeslání a potvrzení e-mailu je
                  na vině doručitelnost, tam pomůže připomínka a návod na přesun do hlavní
                  schránky.
                </p>
              </div>
            )}

            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <p className="mb-4 text-xs leading-relaxed text-slate-500">
                Kroky jdou v pořadí, jak jimi žena prochází. Šířka pruhu je podíl z těch,
                které kvíz otevřely. Odeslání a potvrzení e-mailu se dějí mimo kvíz, měří
                se podle stránek, na kterých žena skončí. Výsledek testu je až za nimi,
                protože odkaz na něj chodí v potvrzovacím e-mailu.
              </p>

              <ol className="space-y-1">
                {rows.map((r, i) => {
                  const kriticky = r.propad >= PRAH_KRITICKY;
                  const varovny = !kriticky && r.propad >= PRAH_VAROVNY;
                  const barva = kriticky ? "#e11d48" : varovny ? "#f59e0b" : BRAND;
                  const jeMail = r.typ === "mail";

                  return (
                    <li key={r.klic}>
                      {i > 0 && (
                        <div className="flex items-center gap-2 py-1 pl-4 text-xs">
                          <ArrowDown className="w-3.5 h-3.5 text-slate-300" />
                          {r.propad > 0 ? (
                            <span className={kriticky ? "font-semibold text-rose-600" : varovny ? "font-medium text-amber-600" : "text-slate-400"}>
                              ubylo {r.propad} %
                            </span>
                          ) : (
                            <span className="text-slate-300">beze ztráty</span>
                          )}
                        </div>
                      )}

                      <div
                        className={`rounded-2xl border p-3.5 ${
                          jeMail ? "border-brand-200 bg-brand-50/50" : "border-slate-200 bg-slate-50/60"
                        }`}
                      >
                        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                          <span className="flex items-center gap-2 font-medium text-slate-900">
                            <span
                              className={`inline-flex h-6 min-w-[1.5rem] items-center justify-center rounded-full px-1.5 text-xs font-semibold ${
                                jeMail ? "bg-brand-600 text-white" : "bg-slate-200 text-slate-600"
                              }`}
                            >
                              {jeMail ? <MailCheck className="h-3.5 w-3.5" /> : r.cislo}
                            </span>
                            {r.popis}
                          </span>
                          <span className="tabular-nums text-sm font-semibold text-slate-800">{r.podil} %</span>
                        </div>

                        <div className="h-2.5 w-full overflow-hidden rounded-full bg-slate-200">
                          <div
                            className="h-full rounded-full transition-all"
                            style={{ width: `${Math.max(r.podil, r.dosahlo > 0 ? 1.5 : 0)}%`, backgroundColor: barva }}
                          />
                        </div>

                        {/* Údaje jako samostatné štítky. V jedné řadě s čárkami se to
                            při zalomení na mobilu rozpadalo a špatně se to četlo. */}
                        <div className="mt-3 flex flex-wrap items-center gap-1.5 text-xs">
                          <Stitek titul="Kolik žen se sem dostalo">
                            <Users className="h-3.5 w-3.5 text-slate-400" />
                            <span className="tabular-nums font-medium text-slate-700">{r.dosahlo}</span>
                            <span className="text-slate-500">{r.dosahlo === 1 ? "žena" : "žen"}</span>
                          </Stitek>

                          {r.dojiti > 0 && (
                            <Stitek titul="Za jak dlouho od otevření kvízu se sem dostaly">
                              <Clock className="h-3.5 w-3.5 text-slate-400" />
                              <span className="text-slate-600">dojdou za</span>
                              <span className="tabular-nums font-medium text-slate-700">{cas(r.dojiti)}</span>
                            </Stitek>
                          )}

                          {r.naOtazce > 0 && (
                            <Stitek titul="Jak dlouho se rozmýšlely nad touto otázkou">
                              <Timer className="h-3.5 w-3.5 text-slate-400" />
                              <span className="text-slate-600">rozmýšlí</span>
                              <span className="tabular-nums font-medium text-slate-700">{cas(r.naOtazce)}</span>
                            </Stitek>
                          )}

                          {jeMail && r.cesta && (
                            <Stitek titul="Stránka, podle které se tenhle krok měří">
                              <span className="font-mono text-[11px] text-slate-500">{r.cesta}</span>
                            </Stitek>
                          )}

                          {/* Na posledním kroku „skončilo" neznamená odpadnutí. Za ním
                              už nic není, takže ty ženy kvíz naopak dotáhly do konce. */}
                          {r.skoncilo > 0 && (
                            r.posledni ? (
                              <Stitek
                                barva="emerald"
                                titul="Došly až sem, dál už cesta nepokračuje"
                              >
                                <span className="tabular-nums font-medium">{r.skoncilo}</span>
                                <span>{r.skoncilo === 1 ? "došla až sem" : "došlo až sem"}</span>
                              </Stitek>
                            ) : (
                              <Stitek barva="rose" titul="Kolik žen se dál už neposunulo">
                                <span className="tabular-nums font-medium">{r.skoncilo}</span>
                                <span>{r.skoncilo === 1 ? "tu skončila" : "tu skončilo"}</span>
                              </Stitek>
                            )
                          )}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </div>
          </>
        )}
      </div>

      {/* ========================================================= KALKULÁTOR */}
      <div className="space-y-4">
        <div className="flex items-center gap-2 pt-2">
          <Calculator className="w-5 h-5 text-brand-600" />
          <h2 className="text-lg font-semibold text-slate-900">Kalkulátor příjmu</h2>
        </div>

        {!maKalkulator && (
          <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
            Zatím žádná data z kalkulátoru. Objeví se, jakmile ho někdo otevře a začne s ním pracovat.
          </div>
        )}

        {maKalkulator && (
          <>
            {kalkulator.navsteva && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm">
                <span className="font-medium text-slate-900">Návštěvnost</span>
                <Delic />
                <span className="text-slate-600">
                  <strong className="tabular-nums text-slate-900">{kalkulator.navsteva.navstevnice}</strong> různých lidí
                </span>
                <Delic />
                <span className="text-slate-600">
                  <strong className="tabular-nums text-slate-900">{kalkulator.navsteva.zobrazeni}</strong> otevření
                </span>
                {kalkulator.navsteva.prumer_scroll != null && (
                  <>
                    <Delic />
                    <span className="text-slate-600">
                      scroll <strong className="tabular-nums text-slate-900">{kalkulator.navsteva.prumer_scroll} %</strong>
                    </span>
                  </>
                )}
              </div>
            )}

            {kalkulator.cas && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm">
                <span className="font-medium text-slate-900">Čas na kalkulátoru</span>
                <Delic />
                <span className="text-slate-600">
                  v průměru <strong className="tabular-nums text-slate-900">{cas(kalkulator.cas.prumer_s) || "0 s"}</strong>
                </span>
                {kalkulator.cas.median_s > 0 && (
                  <>
                    <Delic />
                    <span className="text-slate-600">
                      polovina lidí do <strong className="tabular-nums text-slate-900">{cas(kalkulator.cas.median_s)}</strong>
                    </span>
                  </>
                )}
                <Delic />
                <span className="text-slate-600">
                  celkem <strong className="tabular-nums text-slate-900">{kalkulator.cas.celkem_min} min</strong>
                </span>
              </div>
            )}

            {!!kalkulator.tlacitka.length && (
              <div className="rounded-2xl border border-slate-200 bg-white p-5">
                <div className="mb-1 flex items-center gap-2">
                  <MousePointerClick className="w-4 h-4 text-brand-600" />
                  <h3 className="text-base font-semibold text-slate-900">Co lidé mačkají</h3>
                </div>
                <p className="mb-4 text-xs leading-relaxed text-slate-500">
                  Seřazeno podle počtu kliknutí. Sloupec vpravo říká, kolik různých lidí
                  na to tlačítko sáhlo, takže poznáte rozdíl mezi „mačká to jeden dokola"
                  a „používají to všichni".
                </p>
                <ul className="space-y-2">
                  {kalkulator.tlacitka.map((t, i) => {
                    const max = kalkulator.tlacitka[0].kliku || 1;
                    return (
                      <li key={i}>
                        <div className="flex items-baseline justify-between gap-3 text-sm">
                          <span className="min-w-0 truncate text-slate-800">{t.tlacitko}</span>
                          <span className="flex shrink-0 items-center gap-2 tabular-nums text-slate-500">
                            <span className="font-semibold text-slate-900">{t.kliku}×</span>
                            <Delic />
                            <span>{t.lidi} lidí</span>
                          </span>
                        </div>
                        <div className="mt-1 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                          <div className="h-full rounded-full" style={{ width: `${(t.kliku / max) * 100}%`, backgroundColor: BRAND }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </>
        )}
      </div>

      <p className="px-1 pb-2 text-xs leading-relaxed text-slate-500">
        Kroky kvízu se hlásí hned při zobrazení otázky, takže vidíme i ty, které kvíz nedokončí.
        Kliknutí na kalkulátoru se sbírají po celou dobu návštěvy a odesílají se najednou při odchodu,
        aby měření nezatěžovalo stránku.
      </p>
    </div>
  );
}
